import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Flatpickr from 'react-flatpickr';
import SiteHeader from '../../shared/SiteHeader';
import AdSense from '../../shared/AdSense';
import { useAuth } from '../../AuthContext';
import { apiUrl } from '../../api/client';
import type { ChatMessageDto } from '../../types/ChatMessageDto';
import Spinner from '../../shared/Spinner';
import { createApiClient } from '../../api/ApiClient';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setSelectedDate } from '../../store/devotionalSlice';
import MessageFeedback from '../../shared/MessageFeedback';
import { ApiRoutes } from '../../types/ApiRoutes';
import { setCurrentConversationId } from '../../store/conversationsSlice';
import { isAdsEnabled } from '../../utils/featureFlags';
import 'flatpickr/dist/flatpickr.min.css';
import '../Chat/Chat.css';
import '../Devotional/Devotional.css';
import './DevotionalWithChat.css';

const getTodayDateString = (): string => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const DevotionalWithChat: React.FC = () => {
  const { authorizedFetch } = useAuth();
  const api = useMemo(() => createApiClient(authorizedFetch), [authorizedFetch]);
  const dispatch = useAppDispatch();
  const storedDate = useAppSelector((state) => state.devotional.selectedDate);
  const [selectedDate, setSelectedDateLocal] = useState<string>(() => {
    return storedDate ?? getTodayDateString();
  });
  const [messages, setMessages] = useState<ChatMessageDto[]>([]);
  const [question, setQuestion] = useState('');
  const [loadingDevotional, setLoadingDevotional] = useState(true);
  const [loadingChat, setLoadingChat] = useState(false);
  const [error, setError] = useState('');
  const [adRefreshKey, setAdRefreshKey] = useState(0);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const lastUserMsgRef = useRef<HTMLDivElement | null>(null);
  const chatQuestionRef = useRef<HTMLTextAreaElement | null>(null);
  const isMountedRef = useRef(true);

  const TEXTAREA_MAX_ROWS = 3;
  const isBusy = loadingDevotional || loadingChat;

  useEffect(() => {
    const el = chatQuestionRef.current;
    if (!el) {
      return;
    }

    el.style.height = 'auto';
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20;
    const paddingY =
      parseFloat(getComputedStyle(el).paddingTop) + parseFloat(getComputedStyle(el).paddingBottom);
    const maxHeight = lineHeight * TEXTAREA_MAX_ROWS + paddingY;

    if (el.scrollHeight <= maxHeight) {
      el.style.height = `${el.scrollHeight}px`;
      el.style.overflowY = 'hidden';
    } else {
      el.style.height = `${maxHeight}px`;
      el.style.overflowY = 'auto';
    }
  }, [question]);

  const loadDevotional = useCallback(
    async (date: string) => {
      try {
        setLoadingDevotional(true);
        setLoadingChat(false);
        setError('');
        setMessages([]);
        setConversationId(null);
        dispatch(setCurrentConversationId(null));

        const data = await api.get<ChatMessageDto>(apiUrl(`${ApiRoutes.Devotional}/${date}`), {
          errorMessage: 'Failed to fetch devotional',
        });

        if (!isMountedRef.current) {
          return;
        }

        if (data) {
          setMessages([data]);
          setConversationId(data.conversationId);
          dispatch(setCurrentConversationId(data.conversationId));
        }
      } catch (err: unknown) {
        if (isMountedRef.current) {
          setError(
            err instanceof Error ? err.message : 'An error occurred while loading the devotional.'
          );
        }
      } finally {
        if (isMountedRef.current) {
          setLoadingDevotional(false);
        }
      }
    },
    [api, dispatch]
  );

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    document.title = 'Walk It With Me - Devotional';
  }, []);

  useEffect(() => {
    void loadDevotional(selectedDate);
  }, [loadDevotional, selectedDate]);

  useEffect(() => {
    if (!messages.length) {
      return;
    }

    if (lastUserMsgRef.current) {
      lastUserMsgRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [messages]);

  const handleDateChange = (selectedDates: Date[]) => {
    if (selectedDates.length > 0) {
      const newDate = selectedDates[0].toISOString().split('T')[0];
      setSelectedDateLocal(newDate);
      dispatch(setSelectedDate(newDate));
      setQuestion('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmed = question.trim();
    if (!trimmed || !conversationId) {
      return;
    }

    setLoadingChat(true);
    setError('');
    setAdRefreshKey((current) => current + 1);

    const clientUserMessageId = `client-${crypto.randomUUID?.() || Math.random().toString(36).slice(2)}`;
    setMessages((prev) => [
      ...prev,
      {
        role: 'user',
        content: trimmed,
        messageId: clientUserMessageId,
        conversationId,
      },
    ]);

    setQuestion('');

    try {
      const data = await api.post<Partial<ChatMessageDto> & Record<string, unknown>>(
        apiUrl(ApiRoutes.Chat),
        { conversationId, message: trimmed },
        { errorMessage: 'Failed to fetch chat response' }
      );

      if (!isMountedRef.current) {
        return;
      }

      if (data?.conversationId) {
        setConversationId(data.conversationId);
        dispatch(setCurrentConversationId(data.conversationId));
      }

      setMessages((prev) => [...prev, data as ChatMessageDto]);
    } catch (err: unknown) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingChat(false);
      }
    }
  };

  const adClient = process.env.REACT_APP_ADSENSE_CLIENT_ID || '';
  const adSlotVertical = process.env.REACT_APP_ADSENSE_SLOT_VERTICAL || '';
  const adSlotHorizontal = process.env.REACT_APP_ADSENSE_SLOT_HORIZONTAL || '';

  const lastUserIndex = messages.reduce((acc, message, index) => {
    return message.role === 'user' ? index : acc;
  }, -1);

  return (
    <>
      <SiteHeader />
      <div className="container py-5 chat-page">
        <div className="row">
          <div className="col-12 col-lg-9">
            <div className="chat-body p-3 p-md-4">
              <div className="d-flex flex-wrap justify-content-end align-items-center gap-2 mb-3">
                <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                  <label htmlFor="devotional-date" className="form-label mb-0">
                    Devotional Date
                  </label>
                  <Flatpickr
                    id="devotional-date"
                    className="form-control devotional-date-input"
                    value={selectedDate}
                    onChange={handleDateChange}
                    options={{
                      dateFormat: 'Y-m-d',
                      altInput: true,
                      altFormat: 'F j, Y',
                      maxDate: 'today',
                      allowInput: false,
                    }}
                    disabled={isBusy}
                  />
                </div>
              </div>

              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              <div className="chat-thread mt-2" ref={threadRef} aria-live="polite">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    ref={index === lastUserIndex ? lastUserMsgRef : undefined}
                    className={`chat-msg ${message.role === 'user' ? 'msg-user' : 'msg-assistant'}`}
                    data-message-id={message.messageId}
                  >
                    {message.role === 'user' ? (
                      <div className="bubble">{message.content}</div>
                    ) : (
                      <>
                        <div
                          className="bubble"
                          dangerouslySetInnerHTML={{ __html: message.content }}
                        />
                        {message.messageId && <MessageFeedback message={message} />}
                      </>
                    )}
                  </div>
                ))}

                {adRefreshKey > 0 && isAdsEnabled() && (
                  <div key={adRefreshKey} className="chat-ad-inline d-block d-lg-none">
                    <span className="chat-ad-label">Sponsored</span>
                    <AdSense
                      adClient={adClient}
                      adSlot={adSlotHorizontal}
                      adFormat="horizontal"
                      fullWidthResponsive={true}
                    />
                  </div>
                )}

                <Spinner
                  visible={loadingDevotional && messages.length === 0}
                  text="Generating your personalized devotional…"
                  ariaLabel="Loading devotional"
                />
                <Spinner visible={loadingChat} ariaLabel="Thinking…" />
              </div>

              <form onSubmit={handleSubmit} className="chat-form chat-form-fixed mt-3">
                <div className="container container-sm container-md container-lg container-xl">
                  <div className="row justify-content-center">
                    <div className="col-12 col-lg-10 bg-light chat-form-input">
                      <div className="row g-2 align-items-end">
                        <div className="col">
                          <label htmlFor="chatQuestion" className="visually-hidden">
                            Question
                          </label>
                          <textarea
                            id="chatQuestion"
                            ref={chatQuestionRef}
                            className="form-control resize-none"
                            placeholder="Type your question..."
                            value={question}
                            onChange={(event) => setQuestion(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' && !event.shiftKey) {
                                event.preventDefault();
                                if (!isBusy && question.trim()) {
                                  event.currentTarget.form?.requestSubmit();
                                }
                              }
                            }}
                            rows={1}
                            required
                            disabled={isBusy}
                          />
                        </div>
                        <div className="col-auto d-grid">
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isBusy || !question.trim()}
                            aria-label={loadingChat ? 'Sending…' : 'Send'}
                          >
                            <i className="bi bi-send me-md-2" aria-hidden="true"></i>
                            <span className="d-none d-md-inline">
                              {loadingChat ? 'Sending...' : 'Send'}
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div className="col-lg-3 d-none d-lg-flex flex-column devotional-ad-sidebar">
            {isAdsEnabled() && (
              <AdSense adClient={adClient} adSlot={adSlotVertical} adFormat="auto" />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DevotionalWithChat;
